import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { TimezoneProvider } from './contexts/TimezoneContext';
import { Toaster } from './components/ui/sonner';
import PrairieChat from './components/PrairieChat';
import InstallPWA from './components/InstallPWA';
import { Loader2, RefreshCw, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Connection status checker component
// Optimized: Only checks once per session, persists across navigation and page refreshes
// Uses both memory (globalConnectionStatus) and sessionStorage for reliability
const SESSION_KEY = 'sportsab_connected';

// Check sessionStorage on module load (before any React rendering)
let globalConnectionStatus = (() => {
  try {
    if (typeof window !== 'undefined' && sessionStorage.getItem(SESSION_KEY) === 'true') {
      return 'connected';
    }
  } catch (e) {}
  return null;
})();

function ConnectionChecker({ children }) {
  // Initialize state from globalConnectionStatus
  const [connectionStatus, setConnectionStatus] = useState(() => globalConnectionStatus || 'checking');
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  const checkConnection = async () => {
    // Double-check global status
    if (globalConnectionStatus === 'connected') {
      setConnectionStatus('connected');
      return;
    }
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`${API_URL}/api/health`, { 
        method: 'GET',
        cache: 'no-cache',
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (response.ok) {
        // Set both memory and sessionStorage
        globalConnectionStatus = 'connected';
        try { sessionStorage.setItem(SESSION_KEY, 'true'); } catch (e) {}
        setConnectionStatus('connected');
      } else {
        throw new Error('Health check failed');
      }
    } catch (err) {
      console.error('Connection check failed:', err);
      if (retryCount < maxRetries) {
        setRetryCount(prev => prev + 1);
      } else {
        setConnectionStatus('error');
      }
    }
  };

  useEffect(() => {
    // If already connected globally, just update state
    if (globalConnectionStatus === 'connected') {
      setConnectionStatus('connected');
      return;
    }
    checkConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (connectionStatus === 'checking' && retryCount > 0 && retryCount <= maxRetries) {
      const delay = 1000 * retryCount;
      const timer = setTimeout(checkConnection, delay);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryCount]);

  // Once connected, always show children
  if (connectionStatus === 'connected' || globalConnectionStatus === 'connected') {
    return children;
  }

  if (connectionStatus === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center p-8">
          <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Connecting to server...</h2>
          <p className="text-muted-foreground text-sm">
            {retryCount > 0 ? `Attempt ${retryCount} of ${maxRetries}` : 'Please wait...'}
          </p>
        </div>
      </div>
    );
  }

  if (connectionStatus === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center p-8 max-w-md">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Connection Issue</h2>
          <p className="text-muted-foreground text-sm mb-6">
            Unable to connect to the server. The service may be starting up after a deployment. Please wait a moment and try again.
          </p>
          <button
            onClick={() => {
              setRetryCount(0);
              setConnectionStatus('checking');
              // Clear session storage on manual retry
              try { sessionStorage.removeItem(SESSION_KEY); } catch (e) {}
              globalConnectionStatus = null;
              checkConnection();
            }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return children;
}

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AuthCallback from './pages/AuthCallback';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Board from './pages/Board';
import GameDetail from './pages/GameDetail';
import Alerts from './pages/Alerts';
import Settings from './pages/Settings';
import Admin from './pages/Admin';
import Billing from './pages/Billing';
import AccuracyReport from './pages/AccuracyReport';
import Referral from './pages/Referral';
import Analysis from './pages/Analysis';
import AnalysisComplete from './pages/AnalysisComplete';
import PlayerStats from './pages/PlayerStats';
import Stats from './pages/Stats';
import Notifications from './pages/Notifications';
import SharePoster from './pages/SharePoster';
import PredictionHistory from './pages/PredictionHistory';
import UserDashboard from './pages/UserDashboard';
import AdminDashboard from './pages/AdminDashboard';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import ResponsibleGaming from './pages/ResponsibleGaming';
import Methodology from './pages/Methodology';
import PlayerDetail from './pages/PlayerDetail';
import Contact from './pages/Contact';

// Handle OAuth callback before normal routing
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify" element={<VerifyEmail />} />
      <Route path="/board" element={<Board />} />
      <Route path="/game/:gameId" element={<GameDetail />} />
      <Route path="/alerts" element={<Alerts />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/billing" element={<Billing />} />
      <Route path="/billing/success" element={<Billing />} />
      <Route path="/accuracy" element={<AccuracyReport />} />
      <Route path="/referral" element={<Referral />} />
      <Route path="/analysis" element={<Analysis />} />
      <Route path="/analysis/complete" element={<AnalysisComplete />} />
      <Route path="/player-stats" element={<PlayerStats />} />
      <Route path="/stats" element={<Stats />} />
      <Route path="/notifications" element={<Notifications />} />
      <Route path="/share" element={<SharePoster />} />
      <Route path="/predictions" element={<PredictionHistory />} />
      <Route path="/dashboard" element={<UserDashboard />} />
      <Route path="/admin-dashboard" element={<AdminDashboard />} />
      <Route path="/dashboard/admin" element={<AdminDashboard />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/responsible-gaming" element={<ResponsibleGaming />} />
      <Route path="/methodology" element={<Methodology />} />
      <Route path="/player/:playerId" element={<PlayerDetail />} />
      <Route path="/contact" element={<Contact />} />
      {/* Fallback */}
      <Route path="*" element={<Landing />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <TimezoneProvider>
        <ConnectionChecker>
          <AuthProvider>
            <BrowserRouter>
              <div className="min-h-screen">
                <AppRouter />
                <Toaster position="top-right" richColors />
                <PrairieChat />
                <InstallPWA />
              </div>
            </BrowserRouter>
          </AuthProvider>
        </ConnectionChecker>
      </TimezoneProvider>
    </ThemeProvider>
  );
}

export default App;
